/**
 * PERSONAL CLIP EXPORT SYSTEM
 * --------------------------------------------------------------
 * Replaces the generic "VerseSport flyer" exporter. Every share
 * card produced here belongs to ONE of 8 personal/competitive
 * categories and renders the user's nation, position, progress,
 * reward, rivalry and CTA — never a corporate flyer.
 *
 * Pure Canvas. No deps. No network.
 */

export type PersonalClipKind =
  | "mi_nacion"
  | "mi_ranking"
  | "mi_prediccion"
  | "mi_clip_viral"
  | "batalla_naciones"
  | "mision_completada"
  | "nuevo_badge"
  | "nuevo_nivel";

export interface PersonalClipBase {
  /** ISO-3 or common code; used for flag + label. */
  nation?: string;
  /** Display name of the user (shown as "@handle"). */
  handle?: string;
  /** Position in ranking (1, 2, …). */
  position?: number;
  /** Total competitors for context. */
  totalCompetitors?: number;
  /** 0..100 progress for missions, levels, predictions. */
  progress?: number;
  /** Reward label (XP, badge name, prize). */
  reward?: string;
  /** Rivalry opponent nation (ISO code) for batalla / mi_nacion. */
  rivalNation?: string;
  /** Free headline override. */
  headline?: string;
  /** Free subline override. */
  subline?: string;
  /** Brand accent hex. */
  accentHex?: string;
  /** CTA text shown at the bottom. */
  cta?: string;
}

export interface PersonalClipPayload extends PersonalClipBase {
  kind: PersonalClipKind;
}

// ---------------- Flag helpers ----------------

const ISO3_TO_ISO2: Record<string, string> = {
  ARG: "AR", BRA: "BR", MEX: "MX", USA: "US", ESP: "ES", FRA: "FR", GER: "DE", DEU: "DE",
  ITA: "IT", PRT: "PT", POR: "PT", ENG: "GB", GBR: "GB", NED: "NL", NLD: "NL", BEL: "BE",
  CRO: "HR", HRV: "HR", URU: "UY", COL: "CO", CHL: "CL", PER: "PE", ECU: "EC", VEN: "VE",
  PAR: "PY", BOL: "BO", JPN: "JP", KOR: "KR", AUS: "AU", CAN: "CA", MAR: "MA", SEN: "SN",
  GHA: "GH", NGA: "NG", EGY: "EG", TUN: "TN", SAU: "SA", IRN: "IR", QAT: "QA", DEN: "DK",
  DNK: "DK", SUI: "CH", CHE: "CH", POL: "PL", SRB: "RS", CRC: "CR", PAN: "PA",
};

function toIso2(code?: string): string | null {
  if (!code) return null;
  const c = code.trim().toUpperCase();
  if (c.length === 2) return c;
  if (c.length === 3 && ISO3_TO_ISO2[c]) return ISO3_TO_ISO2[c];
  return null;
}

function flagEmoji(code?: string): string {
  const iso2 = toIso2(code);
  if (!iso2) return "🌎";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + (iso2.charCodeAt(0) - 65),
    A + (iso2.charCodeAt(1) - 65),
  );
}

function nationLabel(code?: string): string {
  if (!code) return "MI NACIÓN";
  return code.trim().toUpperCase();
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// ---------------- Category presets ----------------

interface Preset {
  badge: string;       // top eyebrow tag, e.g. "MI NACIÓN"
  emoji: string;       // dominant hero glyph (overridden by flag for nation-based)
  defaultAccent: string;
  defaultHeadline: (p: PersonalClipPayload) => string;
  defaultSubline: (p: PersonalClipPayload) => string;
  defaultCta: string;
  showFlag: boolean;
  showProgress: boolean;
  showRivalry: boolean;
}

const PRESETS: Record<PersonalClipKind, Preset> = {
  mi_nacion: {
    badge: "🔥 NACIÓN EN LLAMAS",
    emoji: "🌎",
    defaultAccent: "#22c55e",
    defaultHeadline: (p) => `${nationLabel(p.nation)} NO SE RINDE`,
    defaultSubline: (p) =>
      p.position
        ? `Top #${p.position} del mundo · ${p.totalCompetitors ?? 48} naciones en guerra`
        : "Si eres de aquí, demuéstralo",
    defaultCta: "Pelea por tu bandera →",
    showFlag: true,
    showProgress: false,
    showRivalry: false,
  },
  mi_ranking: {
    badge: "🏆 RANKING MUNDIAL",
    emoji: "🏆",
    defaultAccent: "#f59e0b",
    defaultHeadline: (p) =>
      p.position ? `SOY TOP #${p.position} DEL MUNDO` : "ESCALANDO EL RANKING",
    defaultSubline: (p) =>
      p.totalCompetitors
        ? `Por encima de ${p.totalCompetitors.toLocaleString()} fans del planeta`
        : `${p.handle ?? "Fan"} dejando atrás al resto`,
    defaultCta: "A ver si me alcanzas →",
    showFlag: true,
    showProgress: false,
    showRivalry: false,
  },
  mi_prediccion: {
    badge: "🎯 PREDICCIÓN GANADORA",
    emoji: "🎯",
    defaultAccent: "#3b82f6",
    defaultHeadline: (p) => p.headline ?? "LO DIJE ANTES QUE NADIE",
    defaultSubline: (p) =>
      p.reward
        ? `+${p.reward} en el bolsillo · ¿tú qué arriesgaste?`
        : "Acerté el marcador exacto",
    defaultCta: "Atrévete a predecir →",
    showFlag: true,
    showProgress: true,
    showRivalry: false,
  },
  mi_clip_viral: {
    badge: "🔥 CLIP DEL MUNDIAL",
    emoji: "🔥",
    defaultAccent: "#ef4444",
    defaultHeadline: (p) =>
      p.headline ??
      (p.nation
        ? `${nationLabel(p.nation)} HIZO ESTALLAR EL MUNDIAL`
        : "ESTO ROMPIÓ EL MUNDIAL"),
    defaultSubline: (p) =>
      p.subline ??
      (p.position
        ? `Top #${p.position} en el pulso global ahora mismo`
        : "Mira lo que acaba de pasar"),
    defaultCta: "Velo antes que tus amigos →",
    showFlag: true,
    showProgress: false,
    showRivalry: false,
  },
  batalla_naciones: {
    badge: "⚔️ GUERRA DE NACIONES",
    emoji: "⚔️",
    defaultAccent: "#f43f5e",
    defaultHeadline: (p) =>
      `${nationLabel(p.nation)} vs ${nationLabel(p.rivalNation)}`,
    defaultSubline: (p) =>
      typeof p.progress === "number"
        ? `${nationLabel(p.nation)} aplasta con ${Math.round(p.progress)}% del hype`
        : "Esta rivalidad se decide AHORA",
    defaultCta: "Elige tu lado →",
    showFlag: true,
    showProgress: true,
    showRivalry: true,
  },
  mision_completada: {
    badge: "✅ DESAFÍO SUPERADO",
    emoji: "✅",
    defaultAccent: "#10b981",
    defaultHeadline: (p) => p.headline ?? "RETO DESTROZADO",
    defaultSubline: (p) =>
      p.reward ? `Me llevé ${p.reward} · ¿te animas?` : "Otra más en mi vitrina",
    defaultCta: "A ver si tú puedes →",
    showFlag: true,
    showProgress: true,
    showRivalry: false,
  },
  nuevo_badge: {
    badge: "🎖️ BADGE DESBLOQUEADO",
    emoji: "🎖️",
    defaultAccent: "#a855f7",
    defaultHeadline: (p) => (p.reward ?? "LOGRO ÉPICO").toUpperCase(),
    defaultSubline: (p) =>
      `${p.handle ?? "Yo"} ya lo tengo · ¿y tú cuándo?`,
    defaultCta: "Gánate el tuyo →",
    showFlag: true,
    showProgress: false,
    showRivalry: false,
  },
  nuevo_nivel: {
    badge: "⚡ SUBÍ DE NIVEL",
    emoji: "⚡",
    defaultAccent: "#06b6d4",
    defaultHeadline: (p) =>
      p.reward ? `NIVEL ${p.reward.toUpperCase()} DESBLOQUEADO` : "SUBÍ DE NIVEL",
    defaultSubline: (p) =>
      typeof p.progress === "number"
        ? `${Math.round(p.progress)}% al siguiente · imparable`
        : "Imparable rumbo a la cima",
    defaultCta: "Súbete al ranking →",
    showFlag: true,
    showProgress: true,
    showRivalry: false,
  },
};

// ---------------- Renderer ----------------

export async function buildPersonalClip(p: PersonalClipPayload): Promise<string> {
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no-canvas");

  const preset = PRESETS[p.kind];
  const accent = p.accentHex ?? preset.defaultAccent;
  const { r, g, b } = hexToRgb(accent);
  const flag = flagEmoji(p.nation);
  const rivalFlag = preset.showRivalry ? flagEmoji(p.rivalNation) : null;
  const headline = (p.headline ?? preset.defaultHeadline(p)).toUpperCase();
  const subline = p.subline ?? preset.defaultSubline(p);
  const cta = p.cta ?? preset.defaultCta;

  // Background — deep gradient + accent glow
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#06060f");
  bg.addColorStop(1, "#150a18");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W / 2, H * 0.28, 40, W / 2, H * 0.28, 820);
  glow.addColorStop(0, `rgba(${r},${g},${b},0.55)`);
  glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Eyebrow badge
  ctx.textAlign = "center";
  ctx.fillStyle = `rgba(${r},${g},${b},1)`;
  ctx.font = "900 38px system-ui, -apple-system, Segoe UI, Roboto";
  ctx.fillText(`▎ ${preset.badge} ▎`, W / 2, 180);

  // Hero row — flag(s) or emoji
  ctx.font = "240px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji'";
  if (preset.showRivalry && rivalFlag) {
    ctx.fillText(flag, W / 2 - 200, 480);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "900 120px system-ui";
    ctx.fillText("VS", W / 2, 470);
    ctx.font = "240px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji'";
    ctx.fillText(rivalFlag, W / 2 + 200, 480);
  } else if (preset.showFlag && p.nation) {
    ctx.fillText(flag, W / 2, 500);
  } else {
    ctx.fillText(preset.emoji, W / 2, 500);
  }

  // Position chip (for ranking-style cards)
  if (typeof p.position === "number") {
    ctx.fillStyle = `rgba(${r},${g},${b},0.95)`;
    const chipW = 320, chipH = 110, chipX = (W - chipW) / 2, chipY = 600;
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") {
      ctx.roundRect(chipX, chipY, chipW, chipH, 55);
    } else {
      ctx.rect(chipX, chipY, chipW, chipH);
    }
    ctx.fill();
    ctx.fillStyle = "#0a0a0a";
    ctx.font = "900 64px system-ui";
    ctx.fillText(`#${p.position}`, W / 2, chipY + 78);
  }

  // Headline
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 92px system-ui, -apple-system, Segoe UI, Roboto";
  const hLines = wrap(ctx, headline, W - 140);
  const headY = 820;
  hLines.slice(0, 3).forEach((ln, i) => ctx.fillText(ln, W / 2, headY + i * 105));

  // Subline
  ctx.fillStyle = "rgba(255,255,255,0.82)";
  ctx.font = "600 42px system-ui, -apple-system, Segoe UI, Roboto";
  const sLines = wrap(ctx, subline, W - 200);
  const subY = headY + hLines.length * 105 + 40;
  sLines.slice(0, 3).forEach((ln, i) => ctx.fillText(ln, W / 2, subY + i * 58));

  // Progress bar
  if (preset.showProgress && typeof p.progress === "number") {
    const pct = Math.max(0, Math.min(100, p.progress));
    const barW = W - 240, barH = 26, barX = 120, barY = H - 480;
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") ctx.roundRect(barX, barY, barW, barH, 13);
    else ctx.rect(barX, barY, barW, barH);
    ctx.fill();
    const fillW = (barW * pct) / 100;
    const barGrad = ctx.createLinearGradient(barX, 0, barX + barW, 0);
    barGrad.addColorStop(0, `rgba(${r},${g},${b},1)`);
    barGrad.addColorStop(1, "#ffffff");
    ctx.fillStyle = barGrad;
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") ctx.roundRect(barX, barY, fillW, barH, 13);
    else ctx.rect(barX, barY, fillW, barH);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "800 30px system-ui";
    ctx.fillText(`${Math.round(pct)}%`, W / 2, barY - 18);
  }

  // Reward chip (when not used in headline/subline)
  if (p.reward && (p.kind === "mision_completada" || p.kind === "mi_prediccion")) {
    ctx.fillStyle = `rgba(${r},${g},${b},0.2)`;
    const chipW = 520, chipH = 80, chipX = (W - chipW) / 2, chipY = H - 380;
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") ctx.roundRect(chipX, chipY, chipW, chipH, 40);
    else ctx.rect(chipX, chipY, chipW, chipH);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 36px system-ui";
    ctx.fillText(`+ ${p.reward}`, W / 2, chipY + 54);
  }

  // CTA bar
  ctx.fillStyle = `rgba(${r},${g},${b},1)`;
  const ctaW = W - 200, ctaH = 110, ctaX = 100, ctaY = H - 260;
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") ctx.roundRect(ctaX, ctaY, ctaW, ctaH, 55);
  else ctx.rect(ctaX, ctaY, ctaW, ctaH);
  ctx.fill();
  ctx.fillStyle = "#0a0a0a";
  ctx.font = "900 44px system-ui";
  ctx.fillText(cta, W / 2, ctaY + 70);

  // Footer brand mark — small, personal-first
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "700 28px system-ui";
  ctx.fillText(
    p.handle ? `@${p.handle} · reta a tu gente` : "Reta a tu gente",
    W / 2,
    H - 90,
  );

  return canvas.toDataURL("image/png");
}

export function downloadDataUrl(dataUrl: string, filename = "versesport-clip.png") {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function exportAndSharePersonalClip(p: PersonalClipPayload) {
  const dataUrl = await buildPersonalClip(p);
  const filename = `versesport-${p.kind}.png`;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], filename, { type: "image/png" });
    const nav = navigator as Navigator & {
      canShare?: (d: { files?: File[] }) => boolean;
      share?: (d: ShareData & { files?: File[] }) => Promise<void>;
    };
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      await nav.share({
        files: [file],
        title: "VerseSport",
        text: PRESETS[p.kind].defaultHeadline(p),
      });
      return;
    }
  } catch { /* fall through to download */ }
  downloadDataUrl(dataUrl, filename);
}

/**
 * Share via WhatsApp with real mobile + desktop fallbacks:
 *  - If navigator.share supports files → native sheet (user picks WhatsApp).
 *  - Else → download PNG, copy message to clipboard, open wa.me/?text=...
 */
export async function exportAndShareToWhatsApp(
  p: PersonalClipPayload,
  shareUrl?: string,
) {
  const dataUrl = await buildPersonalClip(p);
  const filename = `versesport-${p.kind}.png`;
  const message = [
    PRESETS[p.kind].defaultHeadline(p),
    p.subline ?? PRESETS[p.kind].defaultSubline(p),
    shareUrl ?? (typeof window !== "undefined" ? window.location.origin : "https://versesport.com"),
  ]
    .filter(Boolean)
    .join("\n");

  // Try native share with file first (mobile)
  try {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], filename, { type: "image/png" });
    const nav = navigator as Navigator & {
      canShare?: (d: { files?: File[] }) => boolean;
      share?: (d: ShareData & { files?: File[] }) => Promise<void>;
    };
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      await nav.share({ files: [file], title: "VerseSport", text: message });
      return { channel: "native_share" as const };
    }
  } catch { /* fall through */ }

  // Desktop / unsupported: download PNG, copy text, open WhatsApp web
  downloadDataUrl(dataUrl, filename);
  try { await navigator.clipboard?.writeText(message); } catch { /* ignore */ }
  if (typeof window !== "undefined") {
    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  }
  return { channel: "whatsapp_web" as const };
}

export const PERSONAL_CLIP_LABEL: Record<PersonalClipKind, string> = {
  mi_nacion: "Mi Nación",
  mi_ranking: "Mi Ranking",
  mi_prediccion: "Mi Predicción",
  mi_clip_viral: "Mi Clip Viral",
  batalla_naciones: "Batalla de Naciones",
  mision_completada: "Misión Completada",
  nuevo_badge: "Nuevo Badge",
  nuevo_nivel: "Nuevo Nivel",
};